import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Calculator, Plus, Send, FileText, Euro, Settings, CheckCircle, Clock, Download, RefreshCw, Eye, Trash2, FileSpreadsheet, AlertTriangle, Edit2, Save, X, Users, MailWarning, AlertCircle, CheckCircle2, Receipt, Upload, CreditCard, Banknote, Wallet, Ban, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' },
  // Il tredicesimo non e' un mese: e' l'esercizio straordinario dell'anno, dove
  // stanno le rate che non cadono in un mese (procedimenti, lavori, fondi).
  { value: 13, label: 'Straordinaria' }
];

// Le due voci che si possono aggiungere a una quota gia' emessa. Le altre
// ("Quota ordinaria", "Acqua") nascono dall'emissione e non si toccano a mano:
// sono la rata deliberata. Questi due nomi devono restare identici a quelli che
// il backend accetta, perche' incassi e residui sono indicizzati sulla stringa.
const VOCI_AGGIUNTIVE = ['Rata a consuntivo', 'Rata straordinaria'];

const PERIOD_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  CALCULATED: { label: 'Calcolato', color: 'bg-blue-100 text-blue-800' },
  APPROVED: { label: 'Approvato', color: 'bg-green-100 text-green-800' },
  SENT: { label: 'Inviato', color: 'bg-purple-100 text-purple-800' }
};

export const MonthlyQuotes = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  const condoName = selectedCondominium?.condominium?.name || 'Condominio';
  
  // Data state
  const [periods, setPeriods] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodQuotes, setPeriodQuotes] = useState([]);
  const [quotesError, setQuotesError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // UI state
  const [activeTab, setActiveTab] = useState('periods');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [expensesDialogOpen, setExpensesDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Expenses management state
  const [periodExpenses, setPeriodExpenses] = useState({
    included: [],
    available: [],
    totalIncluded: 0
  });
  
  // Edit quote state
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [editedAmount, setEditedAmount] = useState('');
  // quoteId -> { voce, importo } del modulo "aggiungi voce", uno per quota
  const [voceInCorso, setVoceInCorso] = useState({});
  const [reviewMode, setReviewMode] = useState('all'); // 'all', 'issues', 'ready'
  // filtro per condomino/unità nel mese (richiesta titolare 10/09/2026)
  const [cercaCondomino, setCercaCondomino] = useState('');
  
  // Payment/Receipt upload state (for residents)
  const [uploadReceiptDialogOpen, setUploadReceiptDialogOpen] = useState(false);
  const [selectedQuoteForPayment, setSelectedQuoteForPayment] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'BONIFICO',
    notes: ''
  });
  
  // Online payment state
  // Registrazione degli incassi da parte di chi li riceve (amministratore,
  // contabile, portiere). Chi registra non si digita: lo ricava il server
  // dall'utente collegato.
  const [incassoDialogOpen, setIncassoDialogOpen] = useState(false);
  const [storicoDialogOpen, setStoricoDialogOpen] = useState(false);
  const [quotaIncasso, setQuotaIncasso] = useState(null);
  const [storicoIncassi, setStoricoIncassi] = useState(null);
  const [salvandoIncasso, setSalvandoIncasso] = useState(false);
  const [incassoForm, setIncassoForm] = useState({
    amount: '', payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'BONIFICO', notes: ''
  });

  const [paymentConfig, setPaymentConfig] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showBankDetailsDialog, setShowBankDetailsDialog] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  // Form state
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  // Anno mostrato nell'elenco dei periodi. Gli esercizi precedenti ci sono:
  // prima non si vedevano perche' la pagina chiedeva solo l'anno corrente.
  const [annoSelezionato, setAnnoSelezionato] = useState(String(currentYear));
  const [createForm, setCreateForm] = useState({
    year: currentYear,
    month: currentMonth,
    billing_mode: 'PREVENTIVO',
    budget_id: ''
  });
  
  const [settingsForm, setSettingsForm] = useState({
    send_day: 1,
    include_pdf_attachment: true,
    include_breakdown: true,
    include_payment_link: false,
    email_subject_template: '',
    email_body_template: ''
  });

  // Calculate review statistics
  const getReviewStats = useCallback(() => {
    // Payment status statistics (Dare/Avere)
    const paidQuotes = periodQuotes.filter(q => q.payment_status === 'PAID');
    const pendingQuotes = periodQuotes.filter(q => q.payment_status !== 'PAID' && q.total_amount > 0);
    const pendingApprovalQuotes = periodQuotes.filter(q => q.payment_status === 'PENDING_APPROVAL');
    
    const stats = {
      total: periodQuotes.length,
      withEmail: periodQuotes.filter(q => q.owner_email).length,
      withoutEmail: periodQuotes.filter(q => !q.owner_email).length,
      zeroAmount: periodQuotes.filter(q => !q.total_amount || q.total_amount === 0).length,
      alreadySent: periodQuotes.filter(q => q.email_sent).length,
      readyToSend: periodQuotes.filter(q => q.owner_email && !q.email_sent && q.total_amount > 0).length,
      totalAmount: periodQuotes.reduce((sum, q) => sum + (q.total_amount || 0), 0),
      totalMillesimi: periodQuotes.reduce((sum, q) => sum + (q.millesimi || 0), 0),
      // Riepilogo Dare/Avere
      paidCount: paidQuotes.length,
      paidAmount: paidQuotes.reduce((sum, q) => sum + (q.total_amount || 0), 0),
      pendingCount: pendingQuotes.length,
      pendingAmount: pendingQuotes.reduce((sum, q) => sum + (q.total_amount || 0), 0),
      pendingApprovalCount: pendingApprovalQuotes.length,
      pendingApprovalAmount: pendingApprovalQuotes.reduce((sum, q) => sum + (q.total_amount || 0), 0)
    };
    return stats;
  }, [periodQuotes]);

  // Ordine naturale dei codici unità: A4 prima di A10, non dopo.
  // Con l'ordine alfabetico l'elenco usciva A10, A11, ... A4, A5, e scorrendo
  // i mesi sembrava che le righe si spostassero.
  const ordinaPerUnita = (a, b) => {
    const spezza = (s) => {
      const m = String(s || '').match(/^([A-Za-z]*)(\d*)/);
      return [m?.[1] || '', parseInt(m?.[2] || '0', 10)];
    };
    const [la, na] = spezza(a.unit_number);
    const [lb, nb] = spezza(b.unit_number);
    return la === lb ? na - nb : la.localeCompare(lb);
  };

  // Filter quotes based on review mode
  const getFilteredQuotes = useCallback(() => {
    let righe;
    switch (reviewMode) {
      case 'issues':
        righe = periodQuotes.filter(q => !q.owner_email || q.total_amount === 0);
        break;
      case 'ready':
        righe = periodQuotes.filter(q => q.owner_email && q.total_amount > 0);
        break;
      default:
        righe = periodQuotes;
    }
    const q = cercaCondomino.trim().toLowerCase();
    if (q) {
      righe = righe.filter((x) =>
        (x.owner_name || '').toLowerCase().includes(q) ||
        (x.unit_number || '').toLowerCase() === q ||
        (x.unit_number || '').toLowerCase().startsWith(q));
    }
    return [...righe].sort(ordinaPerUnita);
  }, [periodQuotes, reviewMode, cercaCondomino]);

  // Fetch functions
  // Si scaricano TUTTI i periodi, non solo quelli dell'anno in corso: prima la
  // pagina chiedeva `?year=<anno corrente>` e gli esercizi precedenti restavano
  // invisibili, pur essendo in archivio. La scelta dell'anno si fa qui sotto.
  const fetchPeriods = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPeriods(data.periods || []);
      }
    } catch (error) {
      console.error('Error fetching periods:', error);
    }
  }, [condoId, token]);

  const fetchBudgets = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/budgets?status=APPROVED`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBudgets(data.budgets || data || []);
      }
    } catch (error) {
      console.error('Error fetching budgets:', error);
    }
  }, [condoId, token]);

  const fetchSettings = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setSettingsForm({
          send_day: data.send_day || 1,
          include_pdf_attachment: data.include_pdf_attachment ?? true,
          include_breakdown: data.include_breakdown ?? true,
          include_payment_link: data.include_payment_link ?? false,
          email_subject_template: data.email_subject_template || '',
          email_body_template: data.email_body_template || ''
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, [condoId, token]);

  // D24 — passando da un mese all'altro l'elenco poteva mostrare i condomini
  // del mese precedente: le quote non venivano svuotate prima della nuova
  // richiesta, e due richieste ravvicinate potevano tornare fuori ordine,
  // lasciando in pagina la risposta piu' vecchia. Da qui i nomi che
  // sembravano ripetersi o sparire (Giliberto, Maniscalchi).
  // Ora: si svuota subito, e si accetta solo la risposta del periodo
  // effettivamente richiesto per ultimo.
  const richiestaQuoteCorrente = useRef(null);

  const fetchPeriodQuotes = async (periodId) => {
    richiestaQuoteCorrente.current = periodId;
    setPeriodQuotes([]);
    setQuotesError(null);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${periodId}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (richiestaQuoteCorrente.current !== periodId) return;  // risposta sorpassata
      if (res.ok) {
        const data = await res.json();
        if (richiestaQuoteCorrente.current !== periodId) return;
        setPeriodQuotes(data.quotes || []);
      } else {
        // meglio un elenco vuoto con un avviso che le quote di un altro mese
        setQuotesError('Non è stato possibile caricare le quote di questo periodo.');
      }
    } catch (error) {
      console.error('Error fetching quotes:', error);
      if (richiestaQuoteCorrente.current === periodId) {
        setQuotesError('Errore di connessione durante il caricamento delle quote.');
      }
    }
  };

  // Fetch payment configuration (for residents to see if online payment is available)
  const fetchPaymentConfig = useCallback(async () => {
    if (!condoId || !token) return;
    try {
      // Use public endpoint for residents, full endpoint for admins
      const isUserAdmin = userRole === 'ADMIN';
      const endpoint = isUserAdmin 
        ? `${API_URL}/api/condominiums/${condoId}/payment-config`
        : `${API_URL}/api/condominiums/${condoId}/payment-config/public`;
      
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setPaymentConfig(await res.json());
      }
    } catch (error) {
      console.error('Error fetching payment config:', error);
    }
  }, [condoId, token, userRole]);

  // Initiate online payment (SumUp or bank transfer)
  const handleOnlinePayment = async (quote, method = 'card') => {
    if (!quote) return;
    
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quotes/${quote.id}/create-payment`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          quote_id: quote.id,
          payment_method: method
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (data.checkout_url) {
          // Redirect to SumUp checkout
          window.location.href = data.checkout_url;
        } else if (data.bank_details) {
          // Show bank details dialog
          setBankDetails(data.bank_details);
          setShowBankDetailsDialog(true);
          setShowPaymentDialog(false);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore inizializzazione pagamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Fetch bank details for a quote
  const handleShowBankDetails = async (quote) => {
    if (!quote) return;
    
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quotes/${quote.id}/bank-details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setBankDetails(data);
        setShowBankDetailsDialog(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore caricamento dati bancari');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPeriods(), fetchBudgets(), fetchSettings(), fetchPaymentConfig()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPeriods, fetchBudgets, fetchSettings, fetchPaymentConfig]);

  // Actions
  const handleCreatePeriod = async () => {
    if (createForm.billing_mode === 'PREVENTIVO' && !createForm.budget_id) {
      toast.error('Seleziona un bilancio preventivo');
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('year', createForm.year);
      formData.append('month', createForm.month);
      formData.append('billing_mode', createForm.billing_mode);
      if (createForm.budget_id) {
        formData.append('budget_id', createForm.budget_id);
      }
      
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success('Periodo quote creato');
        setCreateDialogOpen(false);
        fetchPeriods();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore creazione periodo');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const handleCalculateQuotes = async (periodId) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${periodId}/calculate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Quote calcolate: ${data.quotes_created} unità, totale €${data.total_amount}`);
        fetchPeriods();
        if (selectedPeriod?.id === periodId) {
          fetchPeriodQuotes(periodId);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore calcolo quote');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const handleApprovePeriod = async (periodId) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${periodId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Quote approvate');
        fetchPeriods();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore approvazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const handleScheduleSend = async (periodId, sendDate) => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('send_date', sendDate);
      
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${periodId}/schedule-send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success(`Invio schedulato per ${sendDate}`);
        fetchPeriods();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore schedulazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendNow = async (periodId) => {
    if (!window.confirm('Inviare immediatamente le quote a tutti i condomini?')) return;
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${periodId}/send-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // il server dice quante email sono partite davvero: zero inviate non e' un successo
        if ((data.inviate ?? data.emails_queued ?? 0) > 0) {
          toast.success(data.message || 'Invio completato');
        } else {
          toast.warning(data.message || 'Nessuna email inviata', { duration: 8000 });
        }
        fetchPeriods();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore invio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  // Preview quote PDF
  const handlePreviewQuote = async (quote) => {
    if (!selectedPeriod) return;
    
    try {
      const url = `${API_URL}/api/condominiums/${condoId}/quote-periods/${selectedPeriod.id}/quotes/${quote.id}/preview`;
      
      // Open PDF in new tab
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore generazione anteprima');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  // Open receipt upload dialog (for residents)
  const openUploadReceiptDialog = (quote) => {
    setSelectedQuoteForPayment(quote);
    setReceiptFile(null);
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: 'BONIFICO',
      notes: ''
    });
    setUploadReceiptDialogOpen(true);
  };

  // ---- Incassi sulle quote -------------------------------------------------

  const residuoQuota = (q) =>
    Math.round(((q?.total_amount || 0) - (q?.paid_amount || 0)) * 100) / 100;

  // Il residuo di ciascuna voce: quanto vale nel prospetto, meno quanto ne e' stato
  // incassato. Si ricava, non si legge da un campo memorizzato: un valore derivato
  // che viene scritto e' un valore che puo' divergere da cio' da cui deriva.
  const residuoPerVoce = (q) => {
    const incassato = q?.incassato_per_voce || {};
    return (q?.breakdown || []).map((v) => ({
      voce: v.voce,
      dovuto: Math.round((v.importo || 0) * 100) / 100,
      incassato: Math.round((incassato[v.voce] || 0) * 100) / 100,
      residuo: Math.round(((v.importo || 0) - (incassato[v.voce] || 0)) * 100) / 100,
    }));
  };

  const apriRegistraIncasso = (quote) => {
    setQuotaIncasso(quote);
    setIncassoForm({
      amount: residuoQuota(quote).toFixed(2),   // di norma si salda: si propone il residuo
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'BONIFICO',
      notes: ''
    });
    setIncassoDialogOpen(true);
  };

  // Ricevuta numerata di un incasso confermato: la genera il server la prima
  // volta e la restituisce sempre uguale dopo. Vale per amministratore e condomino.
  const scaricaRicevuta = async (p) => {
    if (!quotaIncasso) return;
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quotes/${quotaIncasso.id}/payments/${p.id}/ricevuta`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        toast.error((await res.json().catch(() => ({}))).detail || 'Ricevuta non disponibile');
        return;
      }
      const numero = res.headers.get('X-Receipt-Number');
      const blob = await res.blob();
      window.open(window.URL.createObjectURL(blob), '_blank');
      if (numero && !p.receipt_number) {
        toast.success(`Ricevuta n. ${numero} emessa e salvata in Archivio`);
        await apriStoricoIncassi(quotaIncasso);
      }
    } catch (e) {
      toast.error('Errore di connessione');
    }
  };

  const apriStoricoIncassi = async (quote) => {
    setQuotaIncasso(quote);
    setStoricoIncassi(null);
    setStoricoDialogOpen(true);
    const res = await fetch(
      `${API_URL}/api/condominiums/${condoId}/quotes/${quote.id}/payments`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      setStoricoIncassi(await res.json());
    } else {
      toast.error((await res.json().catch(() => ({}))).detail || 'Non riesco a leggere gli incassi');
      setStoricoDialogOpen(false);
    }
  };

  const registraIncasso = async () => {
    const importo = parseFloat(String(incassoForm.amount).replace(',', '.'));
    if (!importo || importo <= 0) {
      toast.error("L'importo deve essere maggiore di zero");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(incassoForm.payment_date)) {
      toast.error('Indica la data del pagamento');
      return;
    }
    const residuo = residuoQuota(quotaIncasso);
    if (importo > residuo + 0.005 &&
        !window.confirm(
          `Stai registrando ${formatCurrency(importo)} su una quota che ne ha ancora ` +
          `${formatCurrency(residuo)} da incassare. L'eccedenza resterà come credito. Procedo?`)) {
      return;
    }
    setSalvandoIncasso(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quotes/${quotaIncasso.id}/payments`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: importo,
            payment_date: incassoForm.payment_date,
            payment_method: incassoForm.payment_method,
            notes: incassoForm.notes || null
          })
        });
      const dati = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(dati.detail || 'Non sono riuscita a registrare l\'incasso');
        return;
      }
      toast.success(dati.messaggio || 'Incasso registrato');
      setIncassoDialogOpen(false);
      if (selectedPeriod) await fetchPeriodQuotes(selectedPeriod.id);
    } finally {
      setSalvandoIncasso(false);
    }
  };

  const annullaIncasso = async (pagamento) => {
    const motivo = window.prompt(
      'Perché va annullato? Il motivo resta agli atti insieme al tuo nome.');
    if (motivo === null) return;
    if (!motivo.trim()) {
      toast.error('Il motivo è obbligatorio');
      return;
    }
    const res = await fetch(
      `${API_URL}/api/condominiums/${condoId}/quotes/${quotaIncasso.id}/payments/${pagamento.id}/annulla`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`,
                   'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ motivo: motivo.trim() })
      });
    if (!res.ok) {
      toast.error((await res.json().catch(() => ({}))).detail || 'Non sono riuscita ad annullare');
      return;
    }
    toast.success('Incasso annullato');
    await apriStoricoIncassi(quotaIncasso);
    if (selectedPeriod) await fetchPeriodQuotes(selectedPeriod.id);
  };

  const confermaIncasso = async (pagamento) => {
    const res = await fetch(
      `${API_URL}/api/condominiums/${condoId}/quotes/${quotaIncasso.id}/payments/${pagamento.id}/conferma`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      toast.error((await res.json().catch(() => ({}))).detail || 'Non sono riuscita a confermare');
      return;
    }
    toast.success('Incasso confermato');
    await apriStoricoIncassi(quotaIncasso);
    if (selectedPeriod) await fetchPeriodQuotes(selectedPeriod.id);
  };

  // Upload payment receipt
  const handleUploadReceipt = async () => {
    if (!receiptFile || !selectedQuoteForPayment) {
      toast.error('Seleziona un file da caricare');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('receipt_file', receiptFile);
      formData.append('payment_date', paymentForm.payment_date);
      formData.append('payment_method', paymentForm.payment_method);
      if (paymentForm.notes) formData.append('notes', paymentForm.notes);

      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quotes/${selectedQuoteForPayment.id}/payment-receipt`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Ricevuta caricata con successo');
        setUploadReceiptDialogOpen(false);
        
        // Refresh quotes
        if (selectedPeriod) {
          fetchPeriodQuotes(selectedPeriod.id);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore caricamento ricevuta');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  // Save edited quote amount
  const handleSaveQuoteEdit = async (quoteId) => {
    if (!editedAmount || isNaN(parseFloat(editedAmount))) {
      toast.error('Inserire un importo valido');
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('amount', parseFloat(editedAmount));
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quote-periods/${selectedPeriod.id}/quotes/${quoteId}`,
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Importo aggiornato: €${data.new_amount}`);
        
        // Update local state
        setPeriodQuotes(prev => prev.map(q => 
          q.id === quoteId ? { ...q, total_amount: data.new_amount, manually_adjusted: true } : q
        ));
        
        // Update period total
        if (selectedPeriod) {
          setSelectedPeriod(prev => ({ ...prev, total_amount: data.period_total }));
        }
        
        setEditingQuoteId(null);
        setEditedAmount('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  // Aggiunge, cambia o toglie una voce aggiuntiva su una quota.
  //
  // A differenza della modifica dell'importo, qui l'importo cambia PER EFFETTO
  // della voce: si dice quanto vale il conguaglio, non quanto deve valere la
  // quota. Importo 0 toglie la voce. Il server rifiuta se cosi' la quota
  // scenderebbe sotto quello che e' gia' stato incassato, e il suo messaggio
  // dice esattamente quale incasso lo impedisce: si mostra quello.
  const datiVoce = (quote) =>
    voceInCorso[quote.id] || { voce: VOCI_AGGIUNTIVE[0], importo: '' };

  const handleSalvaVoce = async (quote, voce, importo) => {
    const valore = parseFloat(importo);
    if (importo === '' || importo === null || isNaN(valore)) {
      toast.error('Inserire un importo');
      return;
    }
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('voce', voce);
      formData.append('importo', valore);
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quote-periods/${selectedPeriod.id}`
        + `/quotes/${quote.id}/voce`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(
          Math.abs(valore) < 0.005
            ? `${voce}: tolta da ${quote.unit_number}`
            : `${voce} di ${formatCurrency(data.importo)} su ${quote.unit_number}`
        );
        setPeriodQuotes((prev) => prev.map((q) =>
          q.id === quote.id
            ? { ...q, total_amount: data.total_amount, breakdown: data.breakdown,
                manually_adjusted: true }
            : q
        ));
        if (selectedPeriod) {
          setSelectedPeriod((prev) => ({ ...prev, total_amount: data.period_total }));
        }
        setVoceInCorso((prev) => ({ ...prev, [quote.id]: { voce, importo: '' } }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Voce non aggiunta');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  // Start editing a quote
  const startEditQuote = (quote) => {
    setEditingQuoteId(quote.id);
    setEditedAmount(quote.total_amount?.toString() || '0');
  };

  // Cancel editing
  const cancelEditQuote = () => {
    setEditingQuoteId(null);
    setEditedAmount('');
  };

  // Elimina UNA quota, lasciando in piedi tutte le altre del mese.
  //
  // Fino al 19/09/2026 non si poteva: l'unica eliminazione disponibile era quella del
  // periodo intero, che porta via tutte le posizioni del mese. Chi doveva togliere una
  // riga sbagliata -- tipicamente una divisione proprietario/inquilino applicata da
  // prima del subentro -- poteva solo azzerarne l'importo, lasciando a schermo una
  // posizione fantasma da 0,00.
  //
  // Il motivo e' obbligatorio e non e' un fastidio burocratico: il server lo scrive nel
  // log insieme alla quota intera, ed e' cio' che rende l'operazione ricostruibile se
  // ci si accorge dopo di aver sbagliato riga.
  const handleEliminaQuota = async (quote) => {
    const chi = `${quote.unit_number}${quote.owner_name ? ` — ${quote.owner_name}` : ''}`;
    const motivo = window.prompt(
      `Elimina la quota di ${chi} da ${formatCurrency(quote.total_amount)}.\n\n` +
      'Resta nel registro delle operazioni e si può ricostruire.\n' +
      'Perché questa posizione non doveva esistere in questo mese?'
    );
    if (motivo === null) return;                       // ha annullato
    if (!motivo.trim()) {
      toast.error('Serve il motivo: senza, la cancellazione non è ricostruibile');
      return;
    }
    if (!window.confirm(`Confermi l'eliminazione della quota di ${chi}?`)) return;

    setProcessing(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quote-periods/${selectedPeriod.id}`
        + `/quotes/${quote.id}?motivo=${encodeURIComponent(motivo.trim())}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        toast.success(`Quota di ${chi} eliminata`);
        setPeriodQuotes((prev) => prev.filter((q) => q.id !== quote.id));
        if (selectedPeriod) {
          setSelectedPeriod((prev) => ({ ...prev, total_amount: data.period_total }));
        }
      } else {
        const error = await res.json();
        // Il server rifiuta se la quota porta incassi o se e' gia' stata inviata, e il
        // suo messaggio dice quale dei due: si mostra quello, non una frase generica.
        toast.error(error.detail || 'Eliminazione non riuscita');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  // Delete quote period
  const handleDeletePeriod = async (periodId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo periodo di quote? Tutte le quote associate verranno eliminate.')) {
      return;
    }
    
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${periodId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Periodo eliminato');
        fetchPeriods();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveSettings = async () => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('send_day', settingsForm.send_day);
      formData.append('include_pdf_attachment', settingsForm.include_pdf_attachment);
      formData.append('include_breakdown', settingsForm.include_breakdown);
      formData.append('include_payment_link', settingsForm.include_payment_link);
      formData.append('email_subject_template', settingsForm.email_subject_template);
      formData.append('email_body_template', settingsForm.email_body_template);
      
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        toast.success('Impostazioni salvate');
        setSettingsDialogOpen(false);
        fetchSettings();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const openPeriodDetail = async (period) => {
    setSelectedPeriod(period);
    await fetchPeriodQuotes(period.id);
    setDetailDialogOpen(true);
  };

  // Fetch expenses for a period
  const fetchPeriodExpenses = async (periodId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/quote-periods/${periodId}/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPeriodExpenses({
          included: data.included_expenses || [],
          available: data.available_expenses || [],
          totalIncluded: data.total_included || 0
        });
      }
    } catch (error) {
      console.error('Error fetching period expenses:', error);
      toast.error('Errore caricamento spese');
    }
  };

  // Open expenses management dialog
  const openExpensesDialog = async (period) => {
    setSelectedPeriod(period);
    await fetchPeriodExpenses(period.id);
    setExpensesDialogOpen(true);
  };

  // Add expense to period
  const handleAddExpense = async (expenseId) => {
    if (!selectedPeriod) return;
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('expense_id', expenseId);
      
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quote-periods/${selectedPeriod.id}/expenses`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Spesa aggiunta');
        
        // Refresh expenses and periods
        await fetchPeriodExpenses(selectedPeriod.id);
        fetchPeriods();
        
        // Update selected period total
        setSelectedPeriod(prev => ({
          ...prev,
          total_amount: data.new_total_amount,
          status: 'DRAFT'
        }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore aggiunta spesa');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  // Remove expense from period
  const handleRemoveExpense = async (expenseId) => {
    if (!selectedPeriod) return;
    
    if (!window.confirm('Rimuovere questa spesa dal periodo?')) return;
    
    setProcessing(true);
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condoId}/quote-periods/${selectedPeriod.id}/expenses/${expenseId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Spesa rimossa');
        
        // Refresh expenses and periods
        await fetchPeriodExpenses(selectedPeriod.id);
        fetchPeriods();
        
        // Update selected period total
        setSelectedPeriod(prev => ({
          ...prev,
          total_amount: data.new_total_amount,
          status: 'DRAFT'
        }));
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore rimozione spesa');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const anniDisponibili = [...new Set(periods.map(p => p.year))].sort((a, b) => b - a);
  const periodiVisibili = annoSelezionato === 'TUTTI'
    ? [...periods].sort((a, b) => (b.year - a.year) || (b.month - a.month))
    : periods.filter(p => String(p.year) === annoSelezionato)
             .sort((a, b) => a.month - b.month);

  const isAdmin = userRole === 'ADMIN';
  // Chi riceve i soldi puo' registrarli: amministratore, contabile e portiere.
  // Il portiere pero' li registra "da confermare" - lo decide il server, non qui.
  const puoIncassare = ['ADMIN', 'ACCOUNTANT', 'EMPLOYEE'].includes(userRole);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Quote Mensili"
        subtitle="Rate condominiali e ricevute"
        actions={isAdmin ? (
          <>
            <Button variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Impostazioni Email</span>
            </Button>
            <Button className="bg-white text-primary hover:bg-white/90" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Periodo
            </Button>
          </>
        ) : null}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Periodi in archivio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{periods.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Calcolati</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {periods.filter(p => p.status === 'CALCULATED').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Approvati</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {periods.filter(p => p.status === 'APPROVED').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Inviati</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {periods.filter(p => p.status === 'SENT').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Periods Table */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>
                Periodi Quote {annoSelezionato === 'TUTTI' ? '— tutti gli anni' : annoSelezionato}
              </CardTitle>
              <CardDescription>
                Gestisci le rate condominiali mensili
              </CardDescription>
            </div>
            {anniDisponibili.length > 0 && (
              <div className="min-w-[180px]">
                <Label htmlFor="anno-periodi" className="text-xs text-muted-foreground">
                  Anno
                </Label>
                <Select value={annoSelezionato} onValueChange={setAnnoSelezionato}>
                  <SelectTrigger id="anno-periodi"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {anniDisponibili.map((a) => (
                      <SelectItem key={a} value={String(a)}>
                        {a} — {periods.filter(p => p.year === a).length} periodi
                      </SelectItem>
                    ))}
                    <SelectItem value="TUTTI">Tutti gli anni ({periods.length})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {periodiVisibili.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessun periodo quote creato</p>
              {isAdmin && (
                <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crea Primo Periodo
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Periodo</TableHead>
                    <TableHead>Modalità</TableHead>
                    <TableHead className="text-right">Importo Totale</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Data Invio</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {periodiVisibili.map((period) => {
                    const status = PERIOD_STATUS[period.status] || PERIOD_STATUS.DRAFT;
                    const monthName = MONTHS.find(m => m.value === period.month)?.label || period.month;
                    
                    return (
                      <TableRow key={period.id}>
                        <TableCell className="font-medium">
                          {monthName} {period.year}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {period.billing_mode === 'PREVENTIVO'
                              ? 'Preventivo'
                              : period.billing_mode === 'RATA_FISSA'
                              ? 'Rata fissa'
                              : 'Consuntivo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(period.total_amount)}
                        </TableCell>
                        <TableCell>
                          <Badge className={status.color}>{status.label}</Badge>
                        </TableCell>
                        <TableCell>
                          {period.sent_at ? (
                            <span className="text-green-600 text-sm">
                              <CheckCircle className="h-3 w-3 inline mr-1" />
                              {new Date(period.sent_at).toLocaleDateString('it-IT')}
                            </span>
                          ) : period.send_date ? (
                            <span className="text-orange-600 text-sm">
                              <Clock className="h-3 w-3 inline mr-1" />
                              {period.send_date}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openPeriodDetail(period)}
                              title="Visualizza quote"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            
                            {/* Manage Expenses Button - only for CONSUNTIVO or editable periods */}
                            {isAdmin && period.status !== 'SENT' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openExpensesDialog(period)}
                                title="Gestisci spese del periodo"
                              >
                                <Receipt className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {isAdmin && period.status === 'DRAFT' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCalculateQuotes(period.id)}
                                disabled={processing}
                                title="Calcola quote"
                              >
                                <Calculator className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {isAdmin && period.status === 'CALCULATED' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprovePeriod(period.id)}
                                disabled={processing}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {isAdmin && period.status === 'APPROVED' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSendNow(period.id)}
                                disabled={processing}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            
                            {/* Delete Period Button - only for non-SENT periods */}
                            {isAdmin && period.status !== 'SENT' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePeriod(period.id)}
                                disabled={processing}
                                title="Elimina periodo"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Period Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Periodo Quote</DialogTitle>
            <DialogDescription>
              Crea un nuovo periodo per la generazione delle rate condominiali
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Anno</Label>
                <Select
                  value={createForm.year.toString()}
                  onValueChange={(v) => setCreateForm({...createForm, year: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Mese</Label>
                <Select
                  value={createForm.month.toString()}
                  onValueChange={(v) => setCreateForm({...createForm, month: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label>Modalità di Calcolo</Label>
              <Select
                value={createForm.billing_mode}
                onValueChange={(v) => setCreateForm({...createForm, billing_mode: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PREVENTIVO">
                    Preventivo - Rate basate sul bilancio annuale
                  </SelectItem>
                  <SelectItem value="CONSUNTIVO">
                    Consuntivo - Rate basate sulle spese effettive del mese
                  </SelectItem>
                  <SelectItem value="RATA_FISSA">
                    Rata fissa - Importo deliberato per ciascuna unità
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {createForm.billing_mode === 'PREVENTIVO'
                  ? 'Le quote saranno calcolate dividendo il bilancio preventivo per 12 mesi'
                  : createForm.billing_mode === 'RATA_FISSA'
                  ? "Nessun riparto: si emette la rata mensile scritta nell'anagrafica di ogni unità. Serve quando le rate sono già deliberate e non nascono dai soli millesimi (consumi idrici, box, riparti particolari). Le unità senza rata vengono saltate e segnalate."
                  : 'Le quote saranno calcolate sulle fatture caricate nel mese selezionato'}
              </p>
            </div>
            
            {createForm.billing_mode === 'PREVENTIVO' && (
              <div className="grid gap-2">
                <Label>Bilancio Preventivo</Label>
                <Select
                  value={createForm.budget_id}
                  onValueChange={(v) => setCreateForm({...createForm, budget_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona bilancio..." />
                  </SelectTrigger>
                  <SelectContent>
                    {budgets.length === 0 ? (
                      <SelectItem value="_none" disabled>Nessun bilancio approvato</SelectItem>
                    ) : (
                      budgets.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.title || b.financial_year} - {formatCurrency(b.total_expenses)}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreatePeriod} disabled={processing}>
              {processing && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Crea Periodo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Impostazioni Invio Quote</DialogTitle>
            <DialogDescription>
              Configura le opzioni di invio email automatico
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Giorno di Invio Mensile</Label>
              <Select
                value={settingsForm.send_day.toString()}
                onValueChange={(v) => setSettingsForm({...settingsForm, send_day: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(28)].map((_, i) => (
                    <SelectItem key={i+1} value={(i+1).toString()}>
                      {i+1}° giorno del mese
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Allega PDF Bollettino</Label>
                  <p className="text-xs text-muted-foreground">Genera e allega PDF con dettaglio quota</p>
                </div>
                <Switch
                  checked={settingsForm.include_pdf_attachment}
                  onCheckedChange={(v) => setSettingsForm({...settingsForm, include_pdf_attachment: v})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Includi Dettaglio Voci</Label>
                  <p className="text-xs text-muted-foreground">Mostra ripartizione per categoria spesa</p>
                </div>
                <Switch
                  checked={settingsForm.include_breakdown}
                  onCheckedChange={(v) => setSettingsForm({...settingsForm, include_breakdown: v})}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label>Link Pagamento Online</Label>
                  <p className="text-xs text-muted-foreground">Aggiungi link per pagamento (se configurato)</p>
                </div>
                <Switch
                  checked={settingsForm.include_payment_link}
                  onCheckedChange={(v) => setSettingsForm({...settingsForm, include_payment_link: v})}
                />
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label>Oggetto Email</Label>
              <Input
                value={settingsForm.email_subject_template}
                onChange={(e) => setSettingsForm({...settingsForm, email_subject_template: e.target.value})}
                placeholder="Rata condominiale {month}/{year} - {condominium_name}"
              />
              <p className="text-xs text-muted-foreground">
                Variabili: {'{month}'}, {'{year}'}, {'{condominium_name}'}, {'{unit_number}'}, {'{amount}'}
              </p>
            </div>
            
            <div className="grid gap-2">
              <Label>Corpo Email (opzionale)</Label>
              <Textarea
                value={settingsForm.email_body_template}
                onChange={(e) => setSettingsForm({...settingsForm, email_body_template: e.target.value})}
                placeholder="Gentile Condomino, in allegato la rata condominiale..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveSettings} disabled={processing}>
              {processing && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Salva Impostazioni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Period Detail Dialog - Enhanced Review Version */}
      <Dialog open={detailDialogOpen} onOpenChange={(open) => {
        setDetailDialogOpen(open);
        if (!open) {
          setEditingQuoteId(null);
          setEditedAmount('');
          setReviewMode('all');
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Revisione Quote {selectedPeriod && MONTHS.find(m => m.value === selectedPeriod.month)?.label} {selectedPeriod?.year}
            </DialogTitle>
            <DialogDescription>
              Controlla e modifica le quote prima dell'invio • Modalità: {selectedPeriod?.billing_mode === 'PREVENTIVO'
                ? 'Preventivo'
                : selectedPeriod?.billing_mode === 'RATA_FISSA'
                ? 'Rata fissa'
                : 'Consuntivo'}
            </DialogDescription>
          </DialogHeader>
          
          {periodQuotes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Quote non ancora calcolate</p>
              {isAdmin && selectedPeriod?.status === 'DRAFT' && (
                <Button 
                  className="mt-4" 
                  onClick={() => {
                    handleCalculateQuotes(selectedPeriod.id);
                    setDetailDialogOpen(false);
                  }}
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calcola Quote
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Review Statistics Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <Card className="p-3">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-xs text-muted-foreground">Unità</p>
                      <p className="font-bold">{getReviewStats().total}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3">
                  <div className="flex items-center gap-2">
                    <Euro className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-xs text-muted-foreground">Totale Periodo</p>
                      <p className="font-bold">{formatCurrency(getReviewStats().totalAmount)}</p>
                    </div>
                  </div>
                </Card>
                {/* Riepilogo AVERE (Pagato) - Verde */}
                <Card className="p-3 border-green-200 bg-green-50">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="text-xs text-green-700 font-medium">SALDATO (Avere)</p>
                      <p className="font-bold text-green-700">{formatCurrency(getReviewStats().paidAmount)}</p>
                      <p className="text-xs text-green-600">{getReviewStats().paidCount} quote</p>
                    </div>
                  </div>
                </Card>
                {/* Riepilogo DARE (Da Pagare) - Rosso */}
                <Card className="p-3 border-red-200 bg-red-50">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <div>
                      <p className="text-xs text-red-700 font-medium">DA PAGARE (Dare)</p>
                      <p className="font-bold text-red-700">{formatCurrency(getReviewStats().pendingAmount)}</p>
                      <p className="text-xs text-red-600">{getReviewStats().pendingCount} quote</p>
                    </div>
                  </div>
                </Card>
              </div>
              
              {/* Alert per pagamenti in attesa di verifica - Solo Admin */}
              {isAdmin && getReviewStats().pendingApprovalCount > 0 && (
                <Alert className="mb-3 border-yellow-200 bg-yellow-50">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <AlertTitle className="text-yellow-800">Pagamenti in Attesa di Verifica</AlertTitle>
                  <AlertDescription className="text-yellow-700">
                    {getReviewStats().pendingApprovalCount} ricevute di pagamento in attesa di approvazione per un totale di {formatCurrency(getReviewStats().pendingApprovalAmount)}
                  </AlertDescription>
                </Alert>
              )}

              {/* Warning Alerts */}
              {getReviewStats().withoutEmail > 0 && (
                <Alert className="mb-3 border-orange-200 bg-orange-50">
                  <MailWarning className="h-4 w-4 text-orange-600" />
                  <AlertTitle className="text-orange-800">Attenzione: Email mancanti</AlertTitle>
                  <AlertDescription className="text-orange-700">
                    {getReviewStats().withoutEmail} unità non hanno email associata. Le quote non potranno essere inviate automaticamente.
                  </AlertDescription>
                </Alert>
              )}

              {getReviewStats().zeroAmount > 0 && (
                <Alert className="mb-3 border-amber-200 bg-amber-50">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-800">Quote a €0</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    {getReviewStats().zeroAmount} unità hanno importo zero (probabilmente millesimi non assegnati).
                  </AlertDescription>
                </Alert>
              )}

              {/* Filter Tabs */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-2">
                  <Button
                    variant={reviewMode === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setReviewMode('all')}
                  >
                    Tutte ({periodQuotes.length})
                  </Button>
                  <Button
                    variant={reviewMode === 'issues' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setReviewMode('issues')}
                    className={getReviewStats().withoutEmail + getReviewStats().zeroAmount > 0 ? 'border-orange-300' : ''}
                  >
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Problemi ({getReviewStats().withoutEmail + getReviewStats().zeroAmount})
                  </Button>
                  <Button
                    variant={reviewMode === 'ready' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setReviewMode('ready')}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Pronte ({getReviewStats().readyToSend})
                  </Button>
                </div>
                {/* Export CSV - Solo Admin */}
                {isAdmin && (
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>

              {/* Filtro per condomino o unità */}
              <div className="flex items-center gap-2 mb-2">
                <Input
                  placeholder="Cerca condomino o unità (es. Fucarino, A8)…"
                  value={cercaCondomino}
                  onChange={(e) => setCercaCondomino(e.target.value)}
                  className="max-w-sm"
                  data-testid="quote-cerca-condomino"
                />
                {cercaCondomino && (
                  <Button variant="ghost" size="sm" onClick={() => setCercaCondomino('')}>Tutti</Button>
                )}
                <span className="text-xs text-muted-foreground">{getFilteredQuotes().length} quote</span>
              </div>

              {/* Quotes Table */}
              <div className="flex-1 overflow-y-auto border rounded-lg">
                {quotesError && (
                  <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {quotesError} L'elenco resta vuoto di proposito: mostrare le quote
                    di un altro mese sarebbe peggio.
                  </div>
                )}
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>Unità</TableHead>
                      <TableHead>Proprietario</TableHead>
                      <TableHead className="text-right">Millesimi</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                      <TableHead>Stato Pagamento</TableHead>
                      {isAdmin && <TableHead>Email</TableHead>}
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredQuotes().map((quote) => {
                      const hasIssue = !quote.owner_email || quote.total_amount === 0;
                      const isEditing = editingQuoteId === quote.id;
                      const isPaid = quote.payment_status === 'PAID';
                      const isPendingApproval = quote.payment_status === 'PENDING_APPROVAL';
                      
                      return (
                        <TableRow 
                          key={quote.id}
                          className={`${hasIssue ? 'bg-orange-50/50' : ''} ${isPaid ? 'bg-green-50/30' : ''}`}
                        >
                          <TableCell className="font-medium">
                            {quote.unit_number}
                            {quote.manually_adjusted && (
                              <Badge variant="outline" className="ml-2 text-xs">Modificato</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {quote.owner_name || <span className="text-muted-foreground">-</span>}
                            {quote.position_role && (
                              <Badge variant="outline" className="ml-2 text-[10px]" title="Quota per posizione: questa parte della rata spetta a questo soggetto">
                                {{ OWNER: 'proprietario', CO_OWNER: 'comproprietario', USUFRUCTUARY: 'usufruttuario', BARE_OWNER: 'nudo propr.', TENANT: 'inquilino' }[quote.position_role] || quote.position_role}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{quote.millesimi?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell className="text-right">
                            {isEditing ? (
                              <div className="flex items-center justify-end gap-1">
                                <span className="text-muted-foreground">€</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editedAmount}
                                  onChange={(e) => setEditedAmount(e.target.value)}
                                  className="w-24 h-8 text-right"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveQuoteEdit(quote.id);
                                    if (e.key === 'Escape') cancelEditQuote();
                                  }}
                                />
                              </div>
                            ) : (
                              <span className={`font-semibold ${quote.total_amount === 0 ? 'text-orange-600' : ''}`}>
                                {formatCurrency(quote.total_amount)}
                              </span>
                            )}
                            {/* I saldi per voce. Il totale non cambia mai — e' la loro
                                somma — ma un residuo unico non dice se a mancare sia
                                l'ordinaria o l'acqua, e da quello dipende cosa chiedere. */}
                            {!isEditing && residuoPerVoce(quote).length > 0 && (
                              <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                                {residuoPerVoce(quote).map((v) => (
                                  <div key={v.voce} className="flex gap-1 justify-end">
                                    <span>{v.voce.toLowerCase()}</span>
                                    <span className={v.residuo > 0.005 ? 'font-medium text-slate-700' : ''}>
                                      {formatCurrency(v.residuo)}
                                    </span>
                                  </div>
                                ))}
                                {(quote.senza_voce || 0) > 0.005 && (
                                  <div className="flex gap-1 justify-end text-amber-700">
                                    <span>senza voce</span>
                                    <span className="font-medium">
                                      {formatCurrency(quote.senza_voce)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Se i tre valori divergono lo si dice qui: una differenza
                                visibile solo aprendo un popover, in pratica non si vede. */}
                            {!isEditing && quote.importi_confronto && (() => {
                              const valori = ['iniziale', 'ricalcolata', 'da_consuntivo']
                                .map((k) => (quote.importi_confronto[k] || {}).importo)
                                .filter((v) => v !== null && v !== undefined);
                              const divergono =
                                valori.length > 1 && Math.max(...valori) - Math.min(...valori) > 0.01;
                              return divergono ? (
                                <span
                                  className="ml-1 align-middle text-[10px] text-amber-700"
                                  title="Questa quota ha più valori a seconda del documento: apri il dettaglio"
                                  data-testid={`divergenza-${quote.unit_number}`}
                                >
                                  3 valori
                                </span>
                              ) : null;
                            })()}
                            {/* Come e' composta la quota: voce per voce, con tabella e
                                millesimi. E' quello che il condomino chiede quando apre
                                una quota di un esercizio passato. */}
                            {!isEditing && (isAdmin || (Array.isArray(quote.breakdown) && quote.breakdown.length > 0)) && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    type="button"
                                    className="ml-1 inline-flex align-middle text-slate-500 hover:text-primary"
                                    title="Come è composta questa quota"
                                    data-testid={`dettaglio-quota-${quote.unit_number}`}
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-[26rem] max-w-[95vw] p-3 text-sm text-left">
                                  <div className="font-semibold mb-2">
                                    Come è composta la quota di {quote.unit_number}
                                  </div>
                                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                                    {(quote.breakdown || []).map((riga, i) => (
                                      <div key={i} className="flex justify-between gap-3 border-b border-slate-100 pb-1">
                                        <div>
                                          <div className="font-medium">{riga.voce}</div>
                                          {riga.nota && (
                                            <div className="text-xs text-muted-foreground">{riga.nota}</div>
                                          )}
                                        </div>
                                        <div className="whitespace-nowrap font-medium">{formatCurrency(riga.importo)}</div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="flex justify-between mt-2 pt-1 font-semibold">
                                    <span>Totale</span>
                                    <span>{formatCurrency(quote.total_amount)}</span>
                                  </div>
                                  {/* Le due voci che arrivano dopo l'emissione: il
                                      conguaglio che esce dal consuntivo e le rate di un
                                      esercizio straordinario (lavori, fondi, il
                                      Procedimento 25484/2024). Cambiano l'importo della
                                      quota, quindi stanno qui e non fra le voci della
                                      rata deliberata, che non si toccano a mano. */}
                                  {isAdmin && (
                                    <div className="mt-3 pt-2 border-t border-slate-200">
                                      <div className="font-semibold mb-1">Voci aggiuntive</div>
                                      {(quote.breakdown || [])
                                        .filter((r) => VOCI_AGGIUNTIVE.includes(r.voce))
                                        .map((r) => (
                                          <div key={r.voce} className="flex items-center justify-between gap-2 py-0.5">
                                            <span>{r.voce}</span>
                                            <span className="flex items-center gap-2">
                                              <span className="font-medium">{formatCurrency(r.importo)}</span>
                                              <button
                                                type="button"
                                                className="text-xs text-red-700 hover:underline disabled:opacity-40"
                                                disabled={processing}
                                                onClick={() => handleSalvaVoce(quote, r.voce, 0)}
                                                data-testid={`togli-voce-${quote.unit_number}`}
                                              >
                                                togli
                                              </button>
                                            </span>
                                          </div>
                                        ))}
                                      <div className="flex items-center gap-2 mt-1">
                                        <select
                                          className="h-8 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                                          value={datiVoce(quote).voce}
                                          onChange={(e) => setVoceInCorso((prev) => ({
                                            ...prev,
                                            [quote.id]: { ...datiVoce(quote), voce: e.target.value },
                                          }))}
                                          data-testid={`scegli-voce-${quote.unit_number}`}
                                        >
                                          {VOCI_AGGIUNTIVE.map((v) => (
                                            <option key={v} value={v}>{v}</option>
                                          ))}
                                        </select>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          placeholder="0,00"
                                          className="w-24 h-8 text-right"
                                          value={datiVoce(quote).importo}
                                          onChange={(e) => setVoceInCorso((prev) => ({
                                            ...prev,
                                            [quote.id]: { ...datiVoce(quote), importo: e.target.value },
                                          }))}
                                          data-testid={`importo-voce-${quote.unit_number}`}
                                        />
                                        <Button
                                          size="sm"
                                          className="h-8"
                                          disabled={processing}
                                          onClick={() => handleSalvaVoce(
                                            quote, datiVoce(quote).voce, datiVoce(quote).importo)}
                                          data-testid={`aggiungi-voce-${quote.unit_number}`}
                                        >
                                          Applica
                                        </Button>
                                      </div>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Un importo negativo è un accredito. Zero toglie la voce.
                                        La quota non può scendere sotto quanto già incassato.
                                      </p>
                                    </div>
                                  )}
                                  {/* I tre valori della stessa quota. Non e' un dettaglio
                                      contabile: fra il 2021 e il 2024 il condominio ha avuto le
                                      tabelle annullate dalla sentenza 4274/2021, quote chieste sul
                                      bilancio non ricalcolato e nuove tabelle approvate il
                                      07/02/2024. Una quota vale tre cose diverse a seconda del
                                      documento, e chi la apre deve vederle tutte e tre. */}
                                  {quote.importi_confronto && (
                                    <div className="mt-3 pt-2 border-t border-slate-200">
                                      <div className="font-semibold mb-1">I tre valori di questa quota</div>
                                      {[
                                        ['iniziale', 'Iniziale, come fu chiesta'],
                                        ['ricalcolata', 'Ricalcolata sulle tabelle dal 07/02/2024'],
                                        ['da_consuntivo', 'Da consuntivo, quando approvato'],
                                      ].map(([chiave, etichetta]) => {
                                        const v = quote.importi_confronto[chiave] || {};
                                        const efficace = quote.importo_efficace === chiave;
                                        return (
                                          <div
                                            key={chiave}
                                            className={`flex justify-between gap-3 py-1 ${efficace ? 'bg-amber-50 -mx-1 px-1 rounded' : ''}`}
                                            data-testid={`binario-${chiave}-${quote.unit_number}`}
                                          >
                                            <div>
                                              <div className={efficace ? 'font-semibold' : ''}>
                                                {etichetta}
                                                {efficace && (
                                                  <Badge variant="outline" className="ml-2 text-[10px]">in vigore</Badge>
                                                )}
                                                {v.tipo === 'CALCOLATO' && (
                                                  <Badge variant="outline" className="ml-2 text-[10px] text-blue-700">calcolato</Badge>
                                                )}
                                              </div>
                                              {v.nota && (
                                                <div className="text-xs text-muted-foreground">{v.nota}</div>
                                              )}
                                            </div>
                                            <div className="whitespace-nowrap font-medium">
                                              {v.importo === null || v.importo === undefined
                                                ? <span className="text-muted-foreground">non disponibile</span>
                                                : formatCurrency(v.importo)}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                  {quote.breakdown_source && (
                                    <div className="text-xs text-muted-foreground mt-2">Fonte: {quote.breakdown_source}</div>
                                  )}
                                </PopoverContent>
                              </Popover>
                            )}
                          </TableCell>
                          {/* Stato Pagamento - Con colori evidenti */}
                          <TableCell>
                            {isPaid ? (
                              <Badge className="bg-green-500 text-white hover:bg-green-600">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                SALDATO
                              </Badge>
                            ) : isPendingApproval ? (
                              <Badge className="bg-yellow-500 text-white hover:bg-yellow-600">
                                <Clock className="h-3 w-3 mr-1" />
                                In Verifica
                              </Badge>
                            ) : quote.payment_status === 'PARTIAL' ? (
                              /* Parzialmente incassata: si mostra quanto manca, non solo
                                 che manca qualcosa. E' il dato che serve per sollecitare. */
                              <Badge className="bg-orange-500 text-white hover:bg-orange-600">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                ACCONTO — restano {formatCurrency((quote.total_amount || 0) - (quote.paid_amount || 0))}
                              </Badge>
                            ) : quote.total_amount > 0 ? (
                              <Badge className="bg-red-500 text-white hover:bg-red-600">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                DA PAGARE
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-gray-500">
                                -
                              </Badge>
                            )}
                          </TableCell>
                          {/* Email - Solo Admin */}
                          {isAdmin && (
                            <TableCell>
                              {quote.owner_email ? (
                                <span className="text-sm text-muted-foreground truncate max-w-[150px] block">
                                  {quote.owner_email}
                                </span>
                              ) : (
                                <Badge variant="outline" className="text-orange-600 border-orange-300">
                                  <MailWarning className="h-3 w-3 mr-1" />
                                  Mancante
                                </Badge>
                              )}
                            </TableCell>
                          )}
                          {/* Actions column - Preview always visible, Edit only for admin */}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {/* Preview PDF Button - Always visible */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePreviewQuote(quote)}
                                title="Scarica Avviso PDF"
                              >
                                <Eye className="h-4 w-4 text-blue-600" />
                              </Button>

                              {/* Registra incasso - amministratore, contabile e portiere.
                                  Prima non esisteva: l'unico modo di segnare una quota come
                                  pagata era che il condomino caricasse la contabile. */}
                              {puoIncassare && quote.total_amount > 0 && quote.payment_status !== 'PAID' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => apriRegistraIncasso(quote)}
                                  title={`Salda: registra l'incasso del residuo di ${formatCurrency(residuoQuota(quote))} (importo modificabile)`}
                                  className="text-green-700 hover:text-green-800 border-green-300"
                                  data-testid={`salda-${quote.id}`}
                                >
                                  <Wallet className="h-4 w-4 mr-1" />Salda
                                </Button>
                              )}

                              {/* Storico incassi della quota */}
                              {(quote.paid_amount > 0 || (quote.payments || []).length > 0) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => apriStoricoIncassi(quote)}
                                  title="Vedi gli incassi registrati"
                                  className="text-slate-600 hover:text-slate-800"
                                >
                                  <Receipt className="h-4 w-4" />
                                </Button>
                              )}

                              {/* Online Payment Buttons - For residents with unpaid quotes */}
                              {!isAdmin && quote.payment_status !== 'PAID' && quote.payment_status !== 'PENDING_APPROVAL' && quote.total_amount > 0 && (
                                <>
                                  {/* Pay with Card Button - Only if gateway is configured */}
                                  {paymentConfig?.gateway_enabled && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedQuoteForPayment(quote);
                                        handleOnlinePayment(quote, 'card');
                                      }}
                                      title="Paga con Carta"
                                      className="text-blue-600 hover:text-blue-700"
                                      disabled={paymentLoading}
                                    >
                                      <CreditCard className="h-4 w-4" />
                                    </Button>
                                  )}
                                  
                                  {/* Bank Transfer Button - Always available */}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedQuoteForPayment(quote);
                                      handleShowBankDetails(quote);
                                    }}
                                    title="Paga con Bonifico"
                                    className="text-emerald-600 hover:text-emerald-700"
                                  >
                                    <Banknote className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              
                              {/* Upload Receipt Button - For residents to mark as paid */}
                              {!isAdmin && quote.payment_status !== 'PAID' && quote.total_amount > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openUploadReceiptDialog(quote)}
                                  title="Carica ricevuta pagamento"
                                  className="text-green-600 hover:text-green-700"
                                >
                                  <Upload className="h-4 w-4" />
                                </Button>
                              )}
                              
                              {/* Edit buttons - Admin only */}
                              {isAdmin && selectedPeriod?.status !== 'SENT' && (
                                <>
                                  {isEditing ? (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleSaveQuoteEdit(quote.id)}
                                        disabled={processing}
                                      >
                                        <Save className="h-4 w-4 text-green-600" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={cancelEditQuote}
                                      >
                                        <X className="h-4 w-4 text-red-600" />
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => startEditQuote(quote)}
                                        disabled={quote.email_sent}
                                        title="Modifica importo"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                      {/* Elimina questa sola quota. Spento quando ci
                                          sono incassi sopra o la quota e' gia' partita:
                                          il server rifiuterebbe comunque, ma un tasto
                                          spento che dice perche' e' piu' onesto di un
                                          tasto acceso che fallisce. */}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEliminaQuota(quote)}
                                        disabled={
                                          processing
                                          || quote.email_sent
                                          || (quote.paid_amount || 0) > 0.005
                                        }
                                        title={
                                          quote.email_sent
                                            ? 'Già inviata al condomino: si rettifica, non si elimina'
                                            : ((quote.paid_amount || 0) > 0.005
                                              ? 'Ci sono incassi su questa quota: annullarli prima, uno per uno'
                                              : 'Elimina solo questa quota')
                                        }
                                        className="text-red-600 hover:text-red-700"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Millesimi Total Check */}
              <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Totale Millesimi:</span>
                  <span className={`font-medium ${Math.abs(getReviewStats().totalMillesimi - 1000) > 1 ? 'text-orange-600' : 'text-green-600'}`}>
                    {getReviewStats().totalMillesimi.toFixed(2)} / 1000
                    {Math.abs(getReviewStats().totalMillesimi - 1000) > 1 && (
                      <span className="ml-2 text-xs">(differenza: {(getReviewStats().totalMillesimi - 1000).toFixed(2)})</span>
                    )}
                  </span>
                </div>
              </div>
            </>
          )}
          
          <DialogFooter className="mt-4 border-t pt-4">
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Chiudi
            </Button>
            
            {isAdmin && selectedPeriod?.status === 'CALCULATED' && (
              <Button 
                onClick={() => {
                  handleApprovePeriod(selectedPeriod.id);
                  setDetailDialogOpen(false);
                }}
                variant="default"
                disabled={processing}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approva Quote
              </Button>
            )}
            
            {isAdmin && selectedPeriod?.status === 'APPROVED' && (
              <Button 
                onClick={() => {
                  if (getReviewStats().readyToSend === 0) {
                    toast.error('Nessuna quota pronta per l\'invio');
                    return;
                  }
                  handleSendNow(selectedPeriod.id);
                  setDetailDialogOpen(false);
                }}
                disabled={processing || getReviewStats().readyToSend === 0}
              >
                <Send className="h-4 w-4 mr-2" />
                Invia {getReviewStats().readyToSend} Quote
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expenses Management Dialog */}
      <Dialog open={expensesDialogOpen} onOpenChange={(open) => {
        setExpensesDialogOpen(open);
        if (!open) {
          setPeriodExpenses({ included: [], available: [], totalIncluded: 0 });
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Gestione Spese - {selectedPeriod && MONTHS.find(m => m.value === selectedPeriod.month)?.label} {selectedPeriod?.year}
            </DialogTitle>
            <DialogDescription>
              Aggiungi o rimuovi spese dal periodo per modificare l'importo totale delle quote
            </DialogDescription>
          </DialogHeader>
          
          {/* Current Total */}
          <div className="p-4 bg-blue-50 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Totale Spese Incluse</p>
                <p className="text-2xl font-bold text-blue-800">{formatCurrency(periodExpenses.totalIncluded)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Spese nel periodo: {periodExpenses.included.length}</p>
                <p className="text-sm text-muted-foreground">Disponibili: {periodExpenses.available.length}</p>
              </div>
            </div>
            {selectedPeriod?.status !== 'DRAFT' && (
              <Alert className="mt-3 border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-sm">
                  Modificando le spese, le quote calcolate saranno eliminate e dovrai ricalcolarle.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Included Expenses */}
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                Spese Incluse ({periodExpenses.included.length})
              </h4>
              {periodExpenses.included.length === 0 ? (
                <div className="p-4 border rounded-lg text-center text-muted-foreground">
                  Nessuna spesa inclusa nel periodo
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead>Fornitore</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {periodExpenses.included.map((expense) => (
                        <TableRow key={expense.id}>
                          <TableCell className="text-sm">
                            {expense.invoice_date ? new Date(expense.invoice_date).toLocaleDateString('it-IT') : '-'}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {expense.description || expense.invoice_number || '-'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {expense.supplier_name || '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(expense.total)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveExpense(expense.id)}
                              disabled={processing || selectedPeriod?.status === 'SENT'}
                              className="text-red-600 hover:text-red-700"
                              title="Rimuovi dal periodo"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Available Expenses to Add */}
            {periodExpenses.available.length > 0 && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Plus className="h-4 w-4 text-blue-600" />
                  Spese Disponibili da Aggiungere ({periodExpenses.available.length})
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead>Fornitore</TableHead>
                        <TableHead className="text-right">Importo</TableHead>
                        <TableHead className="text-right">Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {periodExpenses.available.map((expense) => (
                        <TableRow key={expense.id} className="bg-gray-50/50">
                          <TableCell className="text-sm">
                            {expense.invoice_date ? new Date(expense.invoice_date).toLocaleDateString('it-IT') : '-'}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {expense.description || expense.invoice_number || '-'}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {expense.supplier_name || '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(expense.total)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleAddExpense(expense.id)}
                              disabled={processing || selectedPeriod?.status === 'SENT'}
                              className="text-green-600 hover:text-green-700"
                              title="Aggiungi al periodo"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-4 border-t pt-4">
            <Button variant="outline" onClick={() => setExpensesDialogOpen(false)}>
              Chiudi
            </Button>
            {selectedPeriod?.status !== 'SENT' && periodExpenses.included.length > 0 && (
              <Button 
                onClick={() => {
                  handleCalculateQuotes(selectedPeriod.id);
                  setExpensesDialogOpen(false);
                }}
                disabled={processing}
              >
                <Calculator className="h-4 w-4 mr-2" />
                Ricalcola Quote
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Receipt Dialog */}
      {/* Registrazione di un incasso su una quota */}
      <Dialog open={incassoDialogOpen} onOpenChange={setIncassoDialogOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Registra un incasso</DialogTitle>
            <DialogDescription>
              {quotaIncasso && (
                <>
                  Unità <strong>{quotaIncasso.unit_number}</strong>
                  {quotaIncasso.owner_name ? ` — ${quotaIncasso.owner_name}` : ''}.
                  Quota di {formatCurrency(quotaIncasso.total_amount)}
                  {quotaIncasso.paid_amount > 0 && (
                    <>, già incassati {formatCurrency(quotaIncasso.paid_amount)},
                      restano <strong>{formatCurrency(residuoQuota(quotaIncasso))}</strong></>
                  )}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="incasso-importo">Importo *</Label>
                <Input id="incasso-importo" type="number" step="0.01" min="0"
                  value={incassoForm.amount}
                  onChange={(e) => setIncassoForm({ ...incassoForm, amount: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="incasso-data">Data del pagamento *</Label>
                <Input id="incasso-data" type="date"
                  value={incassoForm.payment_date}
                  onChange={(e) => setIncassoForm({ ...incassoForm, payment_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Come ha pagato</Label>
              <Select value={incassoForm.payment_method}
                onValueChange={(v) => setIncassoForm({ ...incassoForm, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BONIFICO">Bonifico</SelectItem>
                  <SelectItem value="CONTANTI">Contanti</SelectItem>
                  <SelectItem value="BANCOMAT">Bancomat</SelectItem>
                  <SelectItem value="POS">POS</SelectItem>
                  <SelectItem value="ASSEGNO">Assegno</SelectItem>
                  <SelectItem value="ALTRO">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="incasso-note">Note</Label>
              <Textarea id="incasso-note" rows={2}
                placeholder="Riferimento della contabile, numero assegno, altro…"
                value={incassoForm.notes}
                onChange={(e) => setIncassoForm({ ...incassoForm, notes: e.target.value })} />
            </div>
            {!isAdmin && (
              <Alert>
                <Clock className="h-4 w-4" />
                <AlertDescription>
                  L'incasso resta <strong>in attesa</strong> finché l'amministratore non lo conferma.
                </AlertDescription>
              </Alert>
            )}
            <p className="text-xs text-muted-foreground">
              Chi registra l'incasso viene preso dal tuo accesso: non serve indicarlo,
              e resta agli atti.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIncassoDialogOpen(false)}>Annulla</Button>
            <Button onClick={registraIncasso} disabled={salvandoIncasso}>
              {salvandoIncasso ? 'Salvo…' : 'Registra'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storico degli incassi di una quota */}
      <Dialog open={storicoDialogOpen} onOpenChange={setStoricoDialogOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Incassi registrati</DialogTitle>
            <DialogDescription>
              {storicoIncassi && (
                <>Unità <strong>{storicoIncassi.unita}</strong> — dovuto{' '}
                  {formatCurrency(storicoIncassi.dovuto)}, incassato{' '}
                  {formatCurrency(storicoIncassi.incassato)}, residuo{' '}
                  <strong>{formatCurrency(storicoIncassi.residuo)}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {!storicoIncassi ? (
              <p className="text-sm text-muted-foreground py-4">Carico…</p>
            ) : (storicoIncassi.pagamenti || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">Nessun incasso registrato.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Importo</TableHead>
                    <TableHead>Modo</TableHead>
                    <TableHead>Registrato da</TableHead>
                    <TableHead className="text-right">Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {storicoIncassi.pagamenti.map((p) => (
                    <TableRow key={p.id} className={p.status === 'ANNULLATO' ? 'opacity-50' : ''}>
                      <TableCell>{p.payment_date}</TableCell>
                      <TableCell className="text-right font-medium">
                        <span className={p.status === 'ANNULLATO' ? 'line-through' : ''}>
                          {formatCurrency(p.amount)}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">{p.payment_method}</TableCell>
                      <TableCell className="text-xs">
                        {p.registered_by_name}
                        <span className="text-muted-foreground"> ({p.registered_by_role})</span>
                        {p.status === 'ANNULLATO' && p.void_reason && (
                          <div className="text-xs text-red-600 mt-1">Annullato: {p.void_reason}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {p.status === 'CONFERMATO' ? (
                          <span className="inline-flex items-center gap-1 justify-end">
                            <Badge className="bg-green-500 text-white">confermato</Badge>
                            <Button variant="ghost" size="sm"
                              title={p.receipt_number ? `Ricevuta n. ${p.receipt_number}` : 'Emetti e scarica la ricevuta'}
                              onClick={() => scaricaRicevuta(p)}
                              data-testid={`ricevuta-${p.id}`}>
                              <Receipt className="h-4 w-4 text-slate-700" />
                              {p.receipt_number && <span className="ml-1 text-xs">{p.receipt_number}</span>}
                            </Button>
                          </span>
                        ) : p.status === 'ANNULLATO' ? (
                          <Badge variant="outline" className="text-red-600 border-red-300">annullato</Badge>
                        ) : (
                          <div className="flex gap-1 justify-end items-center">
                            <Badge className="bg-yellow-500 text-white">da confermare</Badge>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" title="Conferma"
                                onClick={() => confermaIncasso(p)}>
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                          </div>
                        )}
                        {isAdmin && p.status !== 'ANNULLATO' && (
                          <Button variant="ghost" size="sm" title="Annulla questo incasso"
                            onClick={() => annullaIncasso(p)}>
                            <Ban className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoricoDialogOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadReceiptDialogOpen} onOpenChange={setUploadReceiptDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Carica Ricevuta Pagamento
            </DialogTitle>
            <DialogDescription>
              Allega la ricevuta del bonifico o del pagamento effettuato. L&apos;amministratore riceverà notifica per la verifica.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Quote Info */}
            {selectedQuoteForPayment && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-600">
                  <strong>Unità:</strong> {selectedQuoteForPayment.unit_identifier || 'N/D'}
                </p>
                <p className="text-lg font-bold text-blue-800">
                  Importo: {formatCurrency(selectedQuoteForPayment.total_amount)}
                </p>
              </div>
            )}
            
            {/* Payment Date */}
            <div className="space-y-2">
              <Label>Data Pagamento *</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
              />
            </div>
            
            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Metodo di Pagamento</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona metodo..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BONIFICO">Bonifico Bancario</SelectItem>
                  <SelectItem value="CARTA">Carta di Credito/Debito</SelectItem>
                  <SelectItem value="CONTANTI">Contanti</SelectItem>
                  <SelectItem value="ASSEGNO">Assegno</SelectItem>
                  <SelectItem value="ALTRO">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Ricevuta/Documento *</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  id="receipt-file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                />
                <label htmlFor="receipt-file" className="cursor-pointer">
                  {receiptFile ? (
                    <div className="flex items-center justify-center gap-2 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                      <span className="text-sm">{receiptFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-muted-foreground">
                      <Upload className="h-8 w-8 mx-auto mb-2" />
                      <p className="text-sm">Clicca per caricare</p>
                      <p className="text-xs">PDF, JPG, PNG (max 5MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Input
                placeholder="Es: CRO bonifico, riferimento pagamento..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadReceiptDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleUploadReceipt}
              disabled={processing || !receiptFile}
            >
              {processing ? 'Caricamento...' : 'Invia Ricevuta'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={showBankDetailsDialog} onOpenChange={setShowBankDetailsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="h-5 w-5 text-emerald-600" />
              Dettagli per Bonifico Bancario
            </DialogTitle>
            <DialogDescription>
              Utilizza questi dati per effettuare il bonifico. Ricorda di inserire la causale corretta.
            </DialogDescription>
          </DialogHeader>
          
          {bankDetails && (
            <div className="space-y-4">
              {/* Bank Name */}
              {bankDetails.bank_name && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Banca</Label>
                  <div className="p-2 bg-gray-50 rounded font-medium">{bankDetails.bank_name}</div>
                </div>
              )}
              
              {/* IBAN */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">IBAN</Label>
                <div className="flex gap-2">
                  <div className="flex-1 p-2 bg-gray-50 rounded font-mono text-sm">
                    {bankDetails.iban || <span className="text-muted-foreground">Non configurato</span>}
                  </div>
                  {bankDetails.iban && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(bankDetails.iban);
                        toast.success('IBAN copiato!');
                      }}
                    >
                      Copia
                    </Button>
                  )}
                </div>
              </div>
              
              {/* BIC */}
              {bankDetails.bic && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">BIC/SWIFT</Label>
                  <div className="p-2 bg-gray-50 rounded font-mono text-sm">{bankDetails.bic}</div>
                </div>
              )}
              
              {/* Beneficiary */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Beneficiario</Label>
                <div className="p-2 bg-gray-50 rounded font-medium">{bankDetails.beneficiary}</div>
              </div>
              
              {/* Amount */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Importo</Label>
                <div className="p-2 bg-emerald-50 rounded font-bold text-emerald-700 text-lg">
                  € {bankDetails.amount?.toFixed(2)}
                </div>
              </div>
              
              {/* Reference */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Causale (importante!)</Label>
                <div className="flex gap-2">
                  <div className="flex-1 p-2 bg-yellow-50 rounded border border-yellow-200 text-sm">
                    {bankDetails.reference}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(bankDetails.reference);
                      toast.success('Causale copiata!');
                    }}
                  >
                    Copia
                  </Button>
                </div>
              </div>
              
              {!bankDetails.is_configured && (
                <Alert className="border-orange-200 bg-orange-50">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertTitle className="text-orange-800">Dati bancari non configurati</AlertTitle>
                  <AlertDescription className="text-orange-700">
                    L&apos;amministratore non ha ancora configurato i dati bancari del condominio. Contatta l&apos;amministratore per i dettagli del pagamento.
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="text-xs text-muted-foreground mt-4">
                Dopo aver effettuato il bonifico, puoi caricare la ricevuta cliccando sull'icona <Upload className="h-3 w-3 inline" /> nella tabella delle quote.
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankDetailsDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MonthlyQuotes;
